import { useState, useEffect, useCallback, useRef } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { usePagination, TablePagination } from"@/components/TablePagination";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Textarea } from"@/components/ui/textarea";
import { Label } from"@/components/ui/label";
import { Badge } from"@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from"@/components/ui/dialog";
import { ScrollArea } from"@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from"@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from"@/components/ui/popover";
import { Calendar } from"@/components/ui/calendar";
import { format } from"date-fns";
import { 
 Plus, Search, Pin, MessageSquare, 
 CheckCircle2, Clock, AlertCircle, 
 Send, Smile, AtSign, Calendar as CalendarIcon, Users,
 Bell, ChevronRight, X, RefreshCw
} from"lucide-react";
import { toast } from"sonner";

// Emoji picker data
const EMOJI_OPTIONS = ["👍","❤️","🎉","😊","🔥","👀","💯","🚀","✅","⭐"];

// Priority colors
const PRIORITY_COLORS = {
 low:"bg-white/60 text-[#334155]",
 medium:"bg-blue-500/10 text-blue-700",
 high:"bg-orange-100 text-orange-700",
 urgent:"bg-rose-500/10 text-rose-700"
};

// Status colors
const STATUS_COLORS = {
 open:"bg-white/60 text-[#334155]",
 in_progress:"bg-blue-500/10 text-blue-700",
 review:"bg-purple-100 text-purple-700",
 done:"bg-emerald-500/10 text-emerald-700",
 archived:"bg-gray-100 text-slate-500"
};

// Category colors
const CATEGORY_COLORS = {
 general:"bg-slate-500",
 construction:"bg-orange-500",
 finance:"bg-green-500",
 sales:"bg-blue-500",
 maintenance:"bg-yellow-500",
 documentation:"bg-purple-500"
};

const TeamTasksPage = ({ user, onLogout }) => {
 const { t, i18n } = useTranslation();
 const [tasks, setTasks] = useState([]);
 const [projects, setProjects] = useState([]);
 const [users, setUsers] = useState([]);
 const [notifications, setNotifications] = useState([]);
 const [unreadCount, setUnreadCount] = useState(0);
 const [stats, setStats] = useState(null);
 const [loading, setLoading] = useState(true);
 const [loadError, setLoadError] = useState(false);
 const [creatingTask, setCreatingTask] = useState(false);
 const [sendingComment, setSendingComment] = useState(false);
 
 // Filters
 const [selectedProject, setSelectedProject] = useState("all");
 const [statusFilter, setStatusFilter] = useState("all");
 const [categoryFilter, setCategoryFilter] = useState("all");
 const [searchQuery, setSearchQuery] = useState("");
 const [showMyTasks, setShowMyTasks] = useState(false);
 
 // Modals
 const [showCreateTask, setShowCreateTask] = useState(false);
 const [showTaskDetail, setShowTaskDetail] = useState(false);
 const [showNotifications, setShowNotifications] = useState(false);
 const [selectedTask, setSelectedTask] = useState(null);
 
 // New task form
 const [newTask, setNewTask] = useState({
 title:"",
 description:"",
 project_id: null,
 assigned_to: [],
 due_date: null,
 priority:"medium",
 category:"general"
 });
 
 // Comment input
 const [commentText, setCommentText] = useState("");
 const [showMentionPopup, setShowMentionPopup] = useState(false);
 const [mentionSearch, setMentionSearch] = useState("");
 const [selectedMentions, setSelectedMentions] = useState([]);
 const commentInputRef = useRef(null);
 const createTaskInFlight = useRef(false);
 const createTaskTrigger = useRef(null);

 // Load data
 const loadTasks = useCallback(async () => {
 setLoadError(false);
 try {
 let url ="/task-items?include_general=true";
 if (selectedProject !=="all") {
 url += `&project_id=${selectedProject}`;
 }
 if (statusFilter !=="all") {
 url += `&status=${statusFilter}`;
 }
 if (categoryFilter !=="all") {
 url += `&category=${categoryFilter}`;
 }
 if (showMyTasks) {
 url +="&assigned_to_me=true";
 }
 
 const response = await axios.get(url);
 setTasks(response.data);
 } catch (error) {
 setTasks([]); setLoadError(true);
 }
 }, [selectedProject, statusFilter, categoryFilter, showMyTasks]);

 const loadProjects = async () => {
 try {
 const response = await axios.get("/task-items/projects");
 setProjects(response.data);
 } catch (error) {
 console.error("Failed to load projects");
 }
 };

 const loadUsers = async () => {
 try {
 const response = await axios.get("/users/list");
 setUsers(response.data);
 } catch (error) {
 console.error("Failed to load users");
 }
 };

 const loadNotifications = async () => {
 try {
 const response = await axios.get("/notifications?limit=20");
 setNotifications(response.data.notifications);
 setUnreadCount(response.data.unread_count);
 } catch (error) {
 console.error("Failed to load notifications");
 }
 };

 const loadStats = async () => {
 try {
 let url ="/task-items/stats";
 if (selectedProject !=="all") {
 url += `?project_id=${selectedProject}`;
 }
 const response = await axios.get(url);
 setStats(response.data);
 } catch (error) {
 console.error("Failed to load stats");
 }
 };

 useEffect(() => {
 const loadAll = async () => {
 setLoading(true);
 await Promise.all([loadProjects(), loadUsers(), loadNotifications()]);
 await loadTasks();
 await loadStats();
 setLoading(false);
 };
 loadAll();
 }, []);

 useEffect(() => {
 if (!loading) {
 loadTasks();
 loadStats();
 }
 }, [selectedProject, statusFilter, categoryFilter, showMyTasks]);

 // Polling for notifications (every 30 seconds)
 useEffect(() => {
 const interval = setInterval(loadNotifications, 30000);
 return () => clearInterval(interval);
 }, []);

 // Create task
 const handleCreateTask = async () => {
 if (createTaskInFlight.current) return;
 if (!newTask.title.trim()) {
 toast.error(t("validation.required"));
 return;
 }
 
 createTaskInFlight.current = true;
 setCreatingTask(true);
 try {
 const taskData = {
 ...newTask,
 project_id: newTask.project_id ==="general"? null : newTask.project_id,
 due_date: newTask.due_date ? new Date(newTask.due_date).toISOString() : null
 };
 
 await axios.post("/task-items", taskData);
 toast.success(t("messages.saveSuccess"), { position: 'bottom-right' });
 setShowCreateTask(false);
 setNewTask({
 title:"",
 description:"",
 project_id: null,
 assigned_to: [],
 due_date: null,
 priority:"medium",
 category:"general"
 });
 loadTasks();
 loadStats();
 } catch (error) {
 toast.error(t('teamTaskAccess.saveFailed'));
 }
 finally { createTaskInFlight.current = false; setCreatingTask(false); }
 };

 // Open task detail
 const handleOpenTask = async (task) => {
 try {
 const response = await axios.get(`/task-items/${task.id}`);
 setSelectedTask(response.data);
 setCommentText(''); setSelectedMentions([]); setShowMentionPopup(false);
 setShowTaskDetail(true);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 // Update task status
 const handleUpdateStatus = async (taskId, newStatus) => {
 try {
 await axios.patch(`/task-items/${taskId}`, { status: newStatus });
 toast.success(t("messages.updateSuccess"));
 loadTasks();
 loadStats();
 if (selectedTask?.id === taskId) {
 const response = await axios.get(`/task-items/${taskId}`);
 setSelectedTask(response.data);
 }
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 // Add comment
 const handleAddComment = async () => {
 if (!commentText.trim() || !selectedTask || sendingComment) return;
 const taskId = selectedTask.id;
 setSendingComment(true);
 
 try {
 // Extract mentions from text (format: @[name](id))
 const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
 const mentions = [];
 let match;
 while ((match = mentionRegex.exec(commentText)) !== null) {
 mentions.push(match[2]); // User ID
 }
 
 // Clean comment text (remove mention markup)
 const cleanContent = commentText.replace(mentionRegex,"@$1");
 
 const response = await axios.post(`/task-items/${selectedTask.id}/comments`, {
 content: cleanContent,
 mentions: mentions
 });
 
 setSelectedTask(prev => prev?.id === taskId ? ({
 ...prev,
 comments: [...(prev.comments || []), response.data]
 }) : prev);
 setCommentText("");
 setSelectedMentions([]);
 toast.success(t("messages.saveSuccess"));
 loadTasks();
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 finally { setSendingComment(false); }
 };

 // Add reaction
 const handleAddReaction = async (taskId, emoji, commentId = null) => {
 try {
 let url = `/task-items/${taskId}/reactions`;
 if (commentId) {
 url += `?comment_id=${commentId}`;
 }
 await axios.post(url, { emoji });
 
 // Refresh task
 const response = await axios.get(`/task-items/${taskId}`);
 setSelectedTask(response.data);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 // Toggle pin
 const handleTogglePin = async (taskId) => {
 try {
 await axios.patch(`/task-items/${taskId}/pin`);
 loadTasks();
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 // Mark notification as read
 const handleMarkNotificationRead = async (notificationId) => {
 try {
 await axios.patch(`/notifications/${notificationId}/read`);
 loadNotifications();
 } catch (error) {
 console.error("Failed to mark notification as read");
 }
 };

 // Mark all read
 const handleMarkAllRead = async () => {
 try {
 await axios.patch("/notifications/read-all");
 loadNotifications();
 } catch (error) {
 console.error("Failed to mark all as read");
 }
 };

 // Handle @ mention in comment
 const handleCommentKeyDown = (e) => {
 if (e.key ==="@") {
 setShowMentionPopup(true);
 setMentionSearch("");
 }
 };

 const handleCommentChange = (e) => {
 const value = e.target.value;
 setCommentText(value);
 
 // Check for @ trigger
 const lastAtIndex = value.lastIndexOf("@");
 if (lastAtIndex !== -1) {
 const textAfterAt = value.slice(lastAtIndex + 1);
 if (!textAfterAt.includes(' ') && textAfterAt.length > 0) {
 setMentionSearch(textAfterAt.toLowerCase());
 setShowMentionPopup(true);
 } else if (textAfterAt ==="") {
 setShowMentionPopup(true);
 setMentionSearch("");
 } else {
 setShowMentionPopup(false);
 }
 } else {
 setShowMentionPopup(false);
 }
 };

 const insertMention = (mentionUser) => {
 const lastAtIndex = commentText.lastIndexOf("@");
 const beforeAt = lastAtIndex >= 0 ? commentText.slice(0, lastAtIndex) : `${commentText}${commentText ? ' ' : ''}`;
 const newText = `${beforeAt}@[${mentionUser.name}](${mentionUser.id}) `;
 setCommentText(newText);
 setSelectedMentions([...selectedMentions, mentionUser.id]);
 setShowMentionPopup(false);
 commentInputRef.current?.focus();
 };
 const mentionUsers = users.filter(person => person.role === 'owner' || person.id === selectedTask?.created_by || selectedTask?.assigned_to?.includes(person.id));
 const refreshConversation = async () => {
   if (!selectedTask) return;
   const id = selectedTask.id;
   try {
     const response = await axios.get(`/task-items/${id}`);
     setSelectedTask(previous => previous?.id === id ? response.data : previous);
     loadTasks(); loadNotifications();
   } catch {
     setShowTaskDetail(false); setSelectedTask(null); setCommentText('');
     toast.error(t('messages.loadFailed'));
   }
 };

 // Filter tasks by search
 const filteredTasks = tasks.filter(task => {
 if (!searchQuery) return true;
 const query = searchQuery.toLowerCase();
 return (
 task.title.toLowerCase().includes(query) ||
 task.description?.toLowerCase().includes(query)
 );
 });

 // Sort: pinned first, then by created_at
 const sortedTasks = [...filteredTasks].sort((a, b) => {
 if (a.is_pinned && !b.is_pinned) return -1;
 if (!a.is_pinned && b.is_pinned) return 1;
 return new Date(b.created_at) - new Date(a.created_at);
 });

 const { currentPage: taskPage, setCurrentPage: setTaskPage, totalPages: taskTotalPages, paginatedItems: paginatedTasks, totalItems: taskTotalItems, startIndex: taskStart, endIndex: taskEnd } = usePagination(sortedTasks);

 const getInitials = (name) => {
 if (!name) return"?";
 return name.split(/\s+/).map(n => n[0]).join("").toUpperCase().slice(0, 2);
 };

 const formatDate = (dateStr) => {
 if (!dateStr || Number.isNaN(new Date(dateStr).getTime())) return '';
 const locale = ({ sq: 'sq-AL', mk: 'mk-MK' })[String(i18n.language).split(/[-_@]/)[0]] || 'en-GB';
 return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(dateStr));
 };

 const formatTime = (dateStr) => {
 if (!dateStr || Number.isNaN(new Date(dateStr).getTime())) return '';
 const locale = ({ sq: 'sq-AL', mk: 'mk-MK' })[String(i18n.language).split(/[-_@]/)[0]] || 'en-GB';
 return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }).format(new Date(dateStr));
 };

 const isOverdue = (task) => {
 if (!task.due_date || task.status ==="done") return false;
 return new Date(task.due_date) < new Date();
 };

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="h-full flex flex-col min-w-0" data-testid="team-tasks-page">
 {/* Header */}
 <div className="py-6 border-b min-w-0">
 <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
 <div>
 <h1 className="font-display text-4xl font-bold uppercase text-slate-900" data-testid="team-tasks-title">
 {t('nav.teamTasks')}
 </h1>
 <p className="text-sm text-slate-500 mt-1" data-testid="team-tasks-visibility">{t(user.role === 'owner' ? 'teamTaskAccess.ownerScope' : 'teamTaskAccess.memberScope')}</p>
 </div>
 <div className="flex items-center gap-3">
 {/* Notifications */}
 <Popover open={showNotifications} onOpenChange={setShowNotifications}>
 <PopoverTrigger asChild>
 <Button variant="outline" size="icon" className="relative" data-testid="team-task-notifications" aria-label={t('teamTaskAccess.notifications')}>
 <Bell className="w-5 h-5"/>
 {unreadCount > 0 && (
 <span data-testid="team-task-unread-count" className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
 {unreadCount > 9 ?"9+": unreadCount}
 </span>
 )}
 </Button>
 </PopoverTrigger>
 <PopoverContent data-testid="team-task-notification-menu" className="w-80 max-w-[calc(100vw-2rem)] p-0" align="end">
 <div className="p-3 border-b flex items-center justify-between">
 <h4 className="font-semibold">{t('teamTaskAccess.notifications')}</h4>
 {unreadCount > 0 && (
 <Button variant="ghost" size="sm" onClick={handleMarkAllRead} data-testid="team-task-mark-all-read">
 {t('teamTaskAccess.markAllRead')}
 </Button>
 )}
 </div>
 <ScrollArea className="h-80">
 {notifications.length > 0 ? (
 <div className="divide-y">
 {notifications.map(notif => (
 <div 
 key={notif.id}
 data-testid={`team-task-notification-${notif.id}`}
 className={`p-3 hover:bg-white/80 cursor-pointer ${!notif.read ? 'bg-blue-50/80' : ''}`}
 onClick={() => {
 handleMarkNotificationRead(notif.id);
 if (notif.task_id) {
 handleOpenTask({ id: notif.task_id });
 setShowNotifications(false);
 }
 }}
 >
 <p className="font-medium text-sm">{notif.title}</p>
 <p className="text-[11px] text-slate-500 mt-1">{notif.message}</p>
 <p className="text-xs text-slate-400 mt-1">{formatTime(notif.created_at)}</p>
 </div>
 ))}
 </div>
 ) : (
 <div className="p-4 text-center text-slate-500 text-sm">
 {t('teamTaskAccess.noNotifications')}
 </div>
 )}
 </ScrollArea>
 </PopoverContent>
 </Popover>
 
 {/* Every role can start a conversation with selected colleagues. */}
 {!user.demoMode && (
 <Button ref={createTaskTrigger} disabled={creatingTask} onClick={() => setShowCreateTask(true)} className="gap-2" data-testid="team-task-create-button">
 <Plus className="w-4 h-4"/>
 {t('teamTaskAccess.newTask')}
 </Button>
 )}
 </div>
 </div>
 
 {/* Stats */}
 {stats && (
 <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
 <Card className="p-3">
 <p className="text-2xl font-bold text-slate-800" data-testid="team-task-stat-total">{stats.total}</p>
 <p className="text-[11px] text-slate-500">{t('teamTaskAccess.totalTasks')}</p>
 </Card>
 <Card className="p-3">
 <p className="text-2xl font-bold text-sm text-slate-500" data-testid="team-task-stat-open">{stats.open}</p>
 <p className="text-[11px] text-slate-500">{t('teamTaskAccess.statuses.open')}</p>
 </Card>
 <Card className="p-3">
 <p className="text-2xl font-bold text-slate-900" data-testid="team-task-stat-in-progress">{stats.in_progress}</p>
 <p className="text-[11px] text-slate-500">{t('teamTaskAccess.statuses.in_progress')}</p>
 </Card>
 <Card className="p-3">
 <p className="text-2xl font-bold text-slate-900" data-testid="team-task-stat-review">{stats.review}</p>
 <p className="text-[11px] text-slate-500">{t('teamTaskAccess.statuses.review')}</p>
 </Card>
 <Card className="p-3">
 <p className="text-2xl font-bold text-slate-900" data-testid="team-task-stat-done">{stats.done}</p>
 <p className="text-[11px] text-slate-500">{t('teamTaskAccess.statuses.done')}</p>
 </Card>
 <Card className="p-3">
 <p className="text-2xl font-bold text-slate-900" data-testid="team-task-stat-overdue">{stats.overdue}</p>
 <p className="text-[11px] text-slate-500">{t('teamTaskAccess.overdue')}</p>
 </Card>
 </div>
 )}
 
 {/* Filters */}
 <div className="flex flex-wrap items-center gap-3">
 <div className="relative flex-1 min-w-[180px] max-w-md">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
 <Input
 placeholder={t('teamTaskAccess.search')} aria-label={t('teamTaskAccess.search')} data-testid="team-task-search"
 value={searchQuery}
 onChange={(e) => setSearchQuery(e.target.value)}
 className="pl-9"
 />
 </div>
 
 <Select value={selectedProject} onValueChange={setSelectedProject}>
 <SelectTrigger className="w-48" data-testid="team-task-project-filter">
 <SelectValue placeholder="Project"/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all" data-testid="team-task-project-all">{t('teamTaskAccess.allProjects')}</SelectItem>
 {projects.map(p => (
 <SelectItem key={p.id} value={p.id} data-testid={`team-task-project-filter-${p.id}`}>{p.name}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 
 <Select value={statusFilter} onValueChange={setStatusFilter}>
 <SelectTrigger className="w-36" data-testid="team-task-status-filter">
 <SelectValue placeholder="Status"/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all" data-testid="team-task-status-all">{t('teamTaskAccess.allStatuses')}</SelectItem>
 {['open', 'in_progress', 'review', 'done'].map(status => <SelectItem key={status} value={status} data-testid={`team-task-status-filter-${status}`}>{t(`teamTaskAccess.statuses.${status}`)}</SelectItem>)}
 </SelectContent>
 </Select>
 
 <Select value={categoryFilter} onValueChange={setCategoryFilter}>
 <SelectTrigger className="w-40" data-testid="team-task-category-filter">
 <SelectValue placeholder="Category"/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all" data-testid="team-task-category-all">{t('teamTaskAccess.allCategories')}</SelectItem>
 {Object.keys(CATEGORY_COLORS).map(category => <SelectItem key={category} value={category} data-testid={`team-task-category-filter-${category}`}>{t(`teamTaskAccess.categories.${category}`)}</SelectItem>)}
 </SelectContent>
 </Select>
 
 <Button 
 variant={showMyTasks ?"default":"outline"}
 data-testid="team-task-assigned-filter"
 onClick={() => setShowMyTasks(!showMyTasks)}
 className="gap-2"
 >
 <Users className="w-4 h-4"/>
 {t('teamTaskAccess.assignedToMe')}
 </Button>
 </div>
 </div>
 
 {/* Task List */}
 <div className="flex-1 py-6 min-w-0">
 {loading ? (
 <div className="flex items-center justify-center h-64">
 <p className="text-slate-500" data-testid="team-task-loading">{t('common.loading')}</p>
 </div>
 ) : loadError ? <div role="alert" data-testid="team-task-load-error"><p>{t('messages.loadFailed')}</p><Button variant="outline" onClick={loadTasks} data-testid="team-task-retry">{t('teamTaskAccess.retry')}</Button></div> : sortedTasks.length > 0 ? (
 <>
 <div className="space-y-3">
 {paginatedTasks.map(task => (
 <Card 
 key={task.id}
 data-testid={`team-task-card-${task.id}`}
 className={`hover:shadow-md transition-all cursor-pointer ${task.is_pinned ? 'border-yellow-400 bg-yellow-50/80/50' : ''}`}
 onClick={() => handleOpenTask(task)}
 >
 <CardContent className="p-4">
 <div className="flex flex-wrap items-start gap-4">
 {/* Category indicator */}
 <div className={`w-1 h-16 shrink-0 rounded-full ${CATEGORY_COLORS[task.category]}`} />
 
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-1">
 {task.is_pinned && <Pin className="w-4 h-4 text-yellow-600"/>}
 <h3 className="font-semibold text-slate-800 truncate">{task.title}</h3>
 </div>
 
 {task.description && (
 <p className="text-sm text-slate-500 line-clamp-2 mb-2">{task.description}</p>
 )}
 
 <div className="flex items-center gap-3 flex-wrap">
 <Badge className={STATUS_COLORS[task.status]}>
 {t(`teamTaskAccess.statuses.${task.status}`, { defaultValue: task.status })}
 </Badge>
 <Badge className={PRIORITY_COLORS[task.priority]}>
 {t(`teamTaskAccess.priorities.${task.priority}`, { defaultValue: task.priority })}
 </Badge>
 {task.project ? (
 <span className="text-[11px] text-slate-500">{task.project.name}</span>
 ) : (
 <span className="text-xs text-slate-400 italic">{t('teamTaskAccess.categories.general')}</span>
 )}
 {task.due_date && (
 <span className={`text-xs flex items-center gap-1 ${isOverdue(task) ? 'text-red-600' : 'text-slate-500'}`}>
 <CalendarIcon className="w-3 h-3"/>
 {formatDate(task.due_date)}
 {isOverdue(task) && <AlertCircle className="w-3 h-3"/>}
 </span>
 )}
 </div>
 </div>
 
 <div className="flex items-center gap-3">
 {/* Assigned users */}
 <div className="flex -space-x-2">
 {task.assigned_users?.slice(0, 3).map(u => (
 <Avatar key={u.id} className="w-8 h-8 border-2 border-white">
 <AvatarFallback className="text-xs bg-blue-500/10 text-blue-700">
 {getInitials(u.name)}
 </AvatarFallback>
 </Avatar>
 ))}
 {task.assigned_users?.length > 3 && (
 <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium">
 +{task.assigned_users.length - 3}
 </div>
 )}
 </div>
 
 {/* Comment count */}
 <div className="flex items-center gap-1 text-slate-400">
 <MessageSquare className="w-4 h-4"/>
 <span className="text-sm">{task.comment_count || 0}</span>
 </div>
 
 <ChevronRight className="w-5 h-5 text-slate-400"/>
 </div>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 <TablePagination currentPage={taskPage} totalPages={taskTotalPages} totalItems={taskTotalItems} startIndex={taskStart} endIndex={taskEnd} onPageChange={setTaskPage} />
 </>
 ) : (
 <div className="flex flex-col items-center justify-center h-64 text-center">
 <MessageSquare className="w-12 h-12 text-slate-300 mb-4"/>
 <h3 className="text-lg font-medium text-slate-600" data-testid="team-task-empty">{t('teamTaskAccess.empty')}</h3>
 <p className="text-slate-500 mt-1">
 {t('teamTaskAccess.startTask')}
 </p>
 </div>
 )}
 </div>
 </div>
 
 {/* Create Task Modal */}
 <Dialog open={showCreateTask} onOpenChange={open => { if (!creatingTask) setShowCreateTask(open); }}>
 <DialogContent className="w-[calc(100%_-_2rem)] max-w-lg" data-testid="team-task-create-dialog" closeLabel={t('common.cancel')}
   onCloseAutoFocus={event => { event.preventDefault(); createTaskTrigger.current?.focus(); }}>
 <DialogHeader>
 <DialogTitle>{t('teamTaskAccess.newTask')}</DialogTitle>
 <DialogDescription>{t('teamTaskAccess.memberScope')}</DialogDescription>
 </DialogHeader>
 
 <div className="space-y-4">
 <div>
 <Label htmlFor="team-task-title">{t('teamTaskAccess.title')} *</Label>
 <Input
 value={newTask.title}
 id="team-task-title" data-testid="team-task-title-input" maxLength={300}
 onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
 placeholder={t('teamTaskAccess.title')}
 />
 </div>
 
 <div>
 <Label htmlFor="team-task-description">{t('teamTaskAccess.description')}</Label>
 <Textarea
 value={newTask.description}
 id="team-task-description" data-testid="team-task-description-input" maxLength={20000}
 onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
 placeholder={t('teamTaskAccess.description')}
 rows={3}
 />
 </div>
 
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div>
 <Label>{t('teamTaskAccess.project')}</Label>
 <Select 
 value={newTask.project_id ||"general"} 
 onValueChange={(v) => setNewTask({ ...newTask, project_id: v ==="general"? null : v })}
 >
 <SelectTrigger data-testid="team-task-project-input">
 <SelectValue placeholder="Select project"/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="general" data-testid="team-task-project-general">{t('teamTaskAccess.generalProject')}</SelectItem>
 {projects.map(p => (
 <SelectItem key={p.id} value={p.id} data-testid={`team-task-project-option-${p.id}`}>{p.name}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 
 <div>
 <Label>{t('teamTaskAccess.category')}</Label>
 <Select value={newTask.category} onValueChange={(v) => setNewTask({ ...newTask, category: v })}>
 <SelectTrigger data-testid="team-task-category-input">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 {Object.keys(CATEGORY_COLORS).map(category => <SelectItem key={category} value={category} data-testid={`team-task-category-option-${category}`}>{t(`teamTaskAccess.categories.${category}`)}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 </div>
 
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div>
 <Label>{t('teamTaskAccess.priority')}</Label>
 <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
 <SelectTrigger data-testid="team-task-priority-input">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 {Object.keys(PRIORITY_COLORS).map(priority => <SelectItem key={priority} value={priority} data-testid={`team-task-priority-option-${priority}`}>{t(`teamTaskAccess.priorities.${priority}`)}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 
 <div>
 <Label htmlFor="team-task-due-date">{t('teamTaskAccess.dueDate')}</Label>
 <Input id="team-task-due-date" data-testid="team-task-due-date" type="date" value={newTask.due_date?.slice(0, 10) || ''} onChange={event => setNewTask({ ...newTask, due_date: event.target.value || null })} />
 </div>
 </div>
 
 <div>
 <Label>{t('teamTaskAccess.assignTo')}</Label>
 <div className="border rounded-sm p-2 max-h-32 overflow-y-auto">
 {users.map(u => (
 <label key={u.id} className="flex items-center gap-2 p-1 hover:bg-white/80 rounded cursor-pointer">
 <input
 type="checkbox"
 data-testid={`team-task-assignee-${u.id}`}
 checked={newTask.assigned_to.includes(u.id)}
 onChange={(e) => {
 if (e.target.checked) {
 setNewTask({ ...newTask, assigned_to: [...newTask.assigned_to, u.id] });
 } else {
 setNewTask({ ...newTask, assigned_to: newTask.assigned_to.filter(id => id !== u.id) });
 }
 }}
 className="rounded"
 />
 <Avatar className="w-6 h-6">
 <AvatarFallback className="text-xs">{getInitials(u.name)}</AvatarFallback>
 </Avatar>
 <span className="text-sm break-words min-w-0">{u.name}</span>
 <span className="text-xs text-slate-400">{t(`teamTaskAccess.roles.${u.role}`, { defaultValue: u.role })}</span>
 </label>
 ))}
 </div>
 </div>
 </div>
 
 <DialogFooter>
 <Button variant="outline" onClick={() => setShowCreateTask(false)} disabled={creatingTask} data-testid="team-task-create-cancel">{t('common.cancel')}</Button>
 <Button onClick={handleCreateTask} disabled={creatingTask || !newTask.title.trim()} data-testid="team-task-create-submit">{creatingTask ? t('common.loading') : t('teamTaskAccess.create')}</Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>
 
 {/* Task Detail Modal */}
 <Dialog open={showTaskDetail} onOpenChange={setShowTaskDetail}>
 <DialogContent data-testid="team-task-detail-dialog" closeLabel={t('common.cancel')} aria-describedby={undefined} className="w-[calc(100%_-_2rem)] max-w-2xl max-h-[90vh] flex flex-col p-0 min-w-0">
 {selectedTask && (
 <>
 <DialogHeader className="p-5 pr-12 pb-0 min-w-0">
 <div className="flex items-start justify-between gap-3">
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-2">
 {selectedTask.is_pinned && <Pin className="w-4 h-4 text-yellow-600"/>}
 <Badge className={STATUS_COLORS[selectedTask.status]}>
 {t(`teamTaskAccess.statuses.${selectedTask.status}`, { defaultValue: selectedTask.status })}
 </Badge>
 <Badge className={PRIORITY_COLORS[selectedTask.priority]}>
 {t(`teamTaskAccess.priorities.${selectedTask.priority}`, { defaultValue: selectedTask.priority })}
 </Badge>
 </div>
 <DialogTitle data-testid="team-task-detail-title" className="text-lg break-words">{selectedTask.title}</DialogTitle>
 {selectedTask.project && (
 <p className="text-sm text-slate-500 mt-1">{selectedTask.project.name}</p>
 )}
 </div>
 
 <Button variant="ghost" size="icon" onClick={refreshConversation} data-testid="team-task-refresh" title={t('teamTaskAccess.refresh')} aria-label={t('teamTaskAccess.refresh')} className="shrink-0"><RefreshCw className="w-4 h-4" /></Button>
 {user?.role ==="owner" && !user.demoMode && (
 <div className="flex items-center gap-2">
 <Button
 variant="ghost"
 size="icon"
 data-testid="team-task-pin" title={t('teamTaskAccess.pin')} aria-label={t('teamTaskAccess.pin')}
 onClick={(e) => { e.stopPropagation(); handleTogglePin(selectedTask.id); }}
 >
 <Pin className={`w-4 h-4 ${selectedTask.is_pinned ? 'text-yellow-600 fill-yellow-600' : ''}`} />
 </Button>
 </div>
 )}
 </div>
 </DialogHeader>
 
 <div className="flex-1 min-h-0 overflow-y-auto px-5">
 <div className="py-4 space-y-4">
 {/* Description */}
 {selectedTask.description && (
 <div>
 <p className="text-sm text-[#334155] whitespace-pre-wrap break-words" data-testid="team-task-detail-description">{selectedTask.description}</p>
 </div>
 )}
 
 {/* Meta info */}
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm [&>div]:min-w-0 [&_p]:break-words">
 <div>
 <p className="text-slate-500">{t('teamTaskAccess.createdBy')}</p>
 <p className="font-medium" data-testid="team-task-detail-creator">{selectedTask.creator?.name}</p>
 </div>
 <div>
 <p className="text-slate-500">{t('teamTaskAccess.created')}</p>
 <p className="font-medium">{formatTime(selectedTask.created_at)}</p>
 </div>
 {selectedTask.due_date && (
 <div>
 <p className="text-slate-500">{t('teamTaskAccess.dueDate')}</p>
 <p className={`font-medium ${isOverdue(selectedTask) ? 'text-red-600' : ''}`}>
 {formatDate(selectedTask.due_date)}
 </p>
 </div>
 )}
 <div>
 <p className="text-slate-500">{t('teamTaskAccess.category')}</p>
 <p className="font-medium capitalize">{t(`teamTaskAccess.categories.${selectedTask.category}`, { defaultValue: selectedTask.category })}</p>
 </div>
 </div>
 
 {/* Assigned users */}
 {selectedTask.assigned_users?.length > 0 && (
 <div>
 <p className="text-sm text-slate-500 mb-2">{t('teamTaskAccess.assignTo')}</p>
 <div className="flex flex-wrap gap-2">
 {selectedTask.assigned_users.map(u => (
 <div key={u.id} data-testid={`team-task-participant-${u.id}`} className="flex items-center gap-2 bg-white/60 rounded-sm px-3 py-1 max-w-full min-w-0">
 <Avatar className="w-5 h-5">
 <AvatarFallback className="text-xs">{getInitials(u.name)}</AvatarFallback>
 </Avatar>
 <span className="text-sm break-words min-w-0">{u.name}</span>
 </div>
 ))}
 </div>
 </div>
 )}
 
 {/* Status update (for assigned users or owner) */}
 {!user.demoMode && (user?.role ==="owner" || selectedTask.created_by === user?.id || selectedTask.assigned_to?.includes(user?.id)) && (
 <div>
 <p className="text-sm text-slate-500 mb-2">{t('teamTaskAccess.updateStatus')}</p>
 <div className="flex flex-wrap gap-2">
 {["open","in_progress","review","done"].map(status => (
 <Button
 key={status}
 data-testid={`team-task-update-status-${status}`}
 variant={selectedTask.status === status ?"default":"outline"}
 size="sm"
 onClick={() => handleUpdateStatus(selectedTask.id, status)}
 className="capitalize"
 >
 {t(`teamTaskAccess.statuses.${status}`)}
 </Button>
 ))}
 </div>
 </div>
 )}
 
 {/* Task reactions */}
 <div className="flex items-center gap-2 flex-wrap">
 {EMOJI_OPTIONS.map(emoji => {
 const count = selectedTask.reactions?.filter(r => r.emoji === emoji && !r.comment_id).length || 0;
 const userReacted = selectedTask.reactions?.some(r => r.emoji === emoji && r.user_id === user?.id && !r.comment_id);
 return count > 0 || userReacted ? (
 <Button
 key={emoji}
 data-testid={`team-task-existing-reaction-${EMOJI_OPTIONS.indexOf(emoji)}`} disabled={user.demoMode}
 variant="ghost"
 size="sm"
 className={`h-7 px-2 ${userReacted ? 'bg-blue-100 ' : ''}`}
 onClick={() => handleAddReaction(selectedTask.id, emoji)}
 >
 {emoji} {count > 0 && count}
 </Button>
 ) : null;
 })}
 <Popover>
 <PopoverTrigger asChild>
 <Button variant="ghost" size="sm" className="h-7 px-2" data-testid="team-task-react" disabled={user.demoMode} title={t('teamTaskAccess.react')} aria-label={t('teamTaskAccess.react')}>
 <Smile className="w-4 h-4"/>
 </Button>
 </PopoverTrigger>
 <PopoverContent className="w-auto p-2">
 <div className="flex gap-1 flex-wrap max-w-48">
 {EMOJI_OPTIONS.map(emoji => (
 <Button
 key={emoji}
 data-testid={`team-task-reaction-choice-${EMOJI_OPTIONS.indexOf(emoji)}`}
 variant="ghost"
 size="sm"
 className="h-8 w-8 p-0"
 onClick={() => handleAddReaction(selectedTask.id, emoji)}
 >
 {emoji}
 </Button>
 ))}
 </div>
 </PopoverContent>
 </Popover>
 </div>
 
 {/* Comments section */}
 <div className="border-t pt-4">
 <h4 className="font-semibold mb-3 flex items-center gap-2">
 <MessageSquare className="w-4 h-4"/>
 {t('teamTaskAccess.comments')} ({selectedTask.comments?.length || 0})
 </h4>
 
 <div className="space-y-4">
 {selectedTask.comments?.map(comment => (
 <div key={comment.id} data-testid={`team-task-comment-${comment.id}`} className="flex gap-3 min-w-0">
 <Avatar className="w-8 h-8 shrink-0">
 <AvatarFallback className="text-xs">
 {getInitials(comment.user?.name)}
 </AvatarFallback>
 </Avatar>
 <div className="flex-1 min-w-0">
 <div className="bg-white/60 rounded-sm p-3">
 <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
 <span className="font-medium text-sm">{comment.user?.name}</span>
 <span className="text-xs text-slate-400">
 {formatTime(comment.created_at)}
 {comment.is_edited && t('teamTaskAccess.edited')}
 </span>
 </div>
 <p className="text-sm whitespace-pre-wrap break-words" data-testid={`team-task-comment-text-${comment.id}`}>{comment.content}</p>
 </div>
 
 {/* Comment reactions */}
 <div className="flex items-center gap-1 mt-1">
 {comment.reactions?.map((r, idx) => (
 <span key={idx} className="text-xs">{r.emoji}</span>
 ))}
 <Popover>
 <PopoverTrigger asChild>
 <Button variant="ghost" size="sm" className="h-6 px-1" data-testid={`team-task-comment-react-${comment.id}`} disabled={user.demoMode} title={t('teamTaskAccess.react')} aria-label={t('teamTaskAccess.react')}>
 <Smile className="w-3 h-3"/>
 </Button>
 </PopoverTrigger>
 <PopoverContent className="w-auto p-2">
 <div className="flex gap-1">
 {EMOJI_OPTIONS.slice(0, 6).map(emoji => (
 <Button
 key={emoji}
 data-testid={`team-task-comment-reaction-${comment.id}-${EMOJI_OPTIONS.indexOf(emoji)}`}
 variant="ghost"
 size="sm"
 className="h-7 w-7 p-0"
 onClick={() => handleAddReaction(selectedTask.id, emoji, comment.id)}
 >
 {emoji}
 </Button>
 ))}
 </div>
 </PopoverContent>
 </Popover>
 </div>
 </div>
 </div>
 ))}
 </div>
 </div>
 </div>
 </div>
 
 {/* Comment input */}
 <div className="p-4 border-t bg-white/50">
 <div className="relative">
 <Textarea
 ref={commentInputRef}
 data-testid="team-task-comment-input" maxLength={20000} aria-label={t('teamTaskAccess.commentPlaceholder')} disabled={sendingComment || user.demoMode}
 value={commentText}
 onChange={handleCommentChange}
 onKeyDown={handleCommentKeyDown}
 placeholder={t('teamTaskAccess.commentPlaceholder')}
 rows={2}
 className="pr-20 resize-none"
 />
 <div className="absolute right-2 bottom-2 flex gap-1">
 <Button
 variant="ghost"
 size="icon"
 className="h-8 w-8"
 onClick={() => setShowMentionPopup(true)}
 data-testid="team-task-mention-button" disabled={sendingComment || user.demoMode} title={t('teamTaskAccess.mention')} aria-label={t('teamTaskAccess.mention')}
 >
 <AtSign className="w-4 h-4"/>
 </Button>
 <Button
 size="icon"
 className="h-8 w-8"
 onClick={handleAddComment}
 data-testid="team-task-send-comment" title={t('teamTaskAccess.send')} aria-label={t('teamTaskAccess.send')}
 disabled={!commentText.trim() || sendingComment || user.demoMode}
 >
 <Send className="w-4 h-4"/>
 </Button>
 </div>
 
 {/* Mention popup */}
 {showMentionPopup && (
 <div className="absolute bottom-full left-0 mb-1 w-64 max-w-full bg-white border rounded-sm shadow-lg z-50" data-testid="team-task-mention-menu">
 <div className="p-2 border-b text-[11px] text-slate-500">{t('teamTaskAccess.selectPerson')}</div>
 <div className="max-h-40 overflow-y-auto">
 {mentionUsers
 .filter(u => !mentionSearch || u.name.toLowerCase().includes(mentionSearch))
 .map(u => (
 <div
 key={u.id}
 data-testid={`team-task-mention-${u.id}`}
 className="flex items-center gap-2 p-2 hover:bg-white/60 cursor-pointer"
 onClick={() => insertMention(u)}
 >
 <Avatar className="w-6 h-6">
 <AvatarFallback className="text-xs">{getInitials(u.name)}</AvatarFallback>
 </Avatar>
 <span className="text-sm">{u.name}</span>
 <span className="text-xs text-slate-400">{t(`teamTaskAccess.roles.${u.role}`, { defaultValue: u.role })}</span>
 </div>
 ))}
 </div>
 </div>
 )}
 </div>
 </div>
 </>
 )}
 </DialogContent>
 </Dialog>
 </Layout>
 );
};

export default TeamTasksPage;
